import {
  isActionCardSnoozed,
  type ActionCardSurface,
  type ChildProgress,
  type DeferredDismissState,
} from '@littlebird/action-cards';
import { cardState } from '@littlebird/action-cards-view';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
} from '@/features/action-cards/analytics';
import { hasUserChat } from '@/features/home/need-you-cards';
import { trackEvent } from '@/utils/analytics';

import {
  reportedSurfaceOf,
  trackCardShown,
  trackCardChatClicked,
  trackCardOpened,
} from '../analytics';
import { CardV2, type CardV2Props } from '../CardV2';
import { type RunEnd } from '../runEvents';
import { useCardHandlers } from '../useCardHandlers';

import {
  FeedCardMoreMenu,
  FeedRowPinSnooze,
  type FeedRowAssign,
} from './FeedCardMoreMenu';
import { FeedCardView, type FeedCardViewProps } from './FeedCardView';
import { FeedDensityContext } from './feedDensity';
import { isActionableStatus } from './feedRowView';
import { useCardPinAndSnooze } from './useCardPinAndSnooze';
import { useCardWatch } from './useCardWatch';
import { useFeedImpression } from './useFeedImpression';

import type { ActionCard, ActionCardPlacement } from '@littlebird/sdk';

export type FeedCardProps = {
  card: ActionCard;
  surface: 'web_home' | 'web_feeds';
  // The row's place in Home or the Feed, the two lists the feed stats cover
  placement?: ActionCardPlacement;
  // A host that draws the feed's card outside the Feed, such as a chat, reports its own surface
  reportedSurface?: ActionCardSurface;
  // The host is a chat, so the card leaves out its Chat button
  inChat?: boolean;
  icon: ReactNode;
  body?: CardV2Props['body'];
  // The row's title, where the host draws one card for several
  title?: string;
  // A line under the title that tells rows with the same title apart
  preview?: string;
  // Replaces the words of a settled row's outcome, where the host draws one card for several
  outcomeLabel?: string;
  todo?: boolean;
  appName: string;
  open: boolean;
  // A parent stays drawn while its child is the active card
  keepOpen?: boolean;
  onToggle: () => void;
  deferredDismiss: DeferredDismissState;
  onDismiss: () => void;
  // Called when a run is pressed, before it starts
  onRetain: () => void;
  // Called once a run press ends, however it ended
  onRunEnd?: (end: RunEnd) => void;
  // The host slides a done card out, so an open frame stays open until it leaves
  leavesWhenDone?: boolean;
  onOpenCardChat: () => void;
  // Set where the host opens the card's chat in place; without it the card's chat opens in a new tab
  onOpenInCardChat?: (seed?: string) => void;
  onOpenSource?: () => void;
  meetingSource?: FeedCardViewProps['meetingSource'];
  // Shown while the card can still be acted on, before the frame's ⋯ and Close
  actions?: ReactNode;
  rowAssign?: FeedRowAssign;
  // The open card answers E, C, ⌫ and ⌘↵ wherever focus is on the page
  keyboardTarget?: boolean;
  // The cards under this one, counted on the row
  subtasks?: ChildProgress;
  // The card's latest activity, shown as its age
  lastActiveAt?: string;
};

// A synced card in Home and the Feed, drawn by FeedCardView
// The state of a row that one card draws for a group of cards: the group's title and preview, and the words of its
// settled status, which the row reads from the run before the outcome
const groupState = (
  state: ReturnType<typeof cardState>,
  title: string,
  preview: string | undefined,
  outcomeLabel: string | undefined
): ReturnType<typeof cardState> => ({
  ...state,
  title,
  row_title: title,
  preview,
  ...(outcomeLabel && {
    run: state.run && { ...state.run, done_text: outcomeLabel },
    outcome: state.outcome && { ...state.outcome, label: outcomeLabel },
  }),
});

export const FeedCard = observer(function FeedCard({
  card,
  surface,
  placement,
  reportedSurface: hostSurface,
  inChat = false,
  icon,
  body,
  title,
  preview,
  outcomeLabel,
  todo = false,
  appName,
  open,
  keepOpen = false,
  onToggle,
  deferredDismiss,
  onDismiss,
  onRetain,
  onRunEnd,
  leavesWhenDone = false,
  onOpenCardChat,
  onOpenInCardChat,
  onOpenSource,
  meetingSource,
  actions,
  rowAssign,
  keyboardTarget = false,
  subtasks,
  lastActiveAt,
}: FeedCardProps) {
  // React Compiler would cache `card.row` on the stable MobX card and freeze the row
  'use no memo';
  const reportedSurface = hostSurface ?? reportedSurfaceOf(surface);
  const density = useContext(FeedDensityContext);
  const framed = open || keepOpen;
  const cardExpanded = framed;
  const lastTrackedExpanded = useRef(cardExpanded);
  useEffect(() => {
    lastTrackedExpanded.current = open || keepOpen;
  }, [open, keepOpen]);
  const toggle = () => {
    const nextOpen = !open;
    const nextExpanded = keepOpen || nextOpen;
    if (lastTrackedExpanded.current !== nextExpanded) {
      lastTrackedExpanded.current = nextExpanded;
      trackEvent('ACTION_CARD_EXPANDED', {
        ...actionCardAnalyticsSnapshot(card, reportedSurface, {
          card_expanded: nextExpanded,
        }),
        expanded: nextExpanded,
      });
    }
    onToggle();
  };
  const previousFramed = useRef(framed);
  const { handlers, completeElsewhere } = useCardHandlers(card, surface, {
    deferredDismiss,
    reportedSurface,
    openCardChat: onOpenInCardChat,
    cardExpanded,
  });
  const { togglePin } = useCardPinAndSnooze(card, reportedSurface);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const watch = useCardWatch(card, {
    reported: reportedSurface,
    todo,
    onDismiss,
    cardExpanded,
  });

  // Shown once with its row, so opening and closing it is not another impression
  useEffect(() => {
    trackCardShown(
      card,
      reportedSurface,
      density,
      { card_expanded: cardExpanded },
      placement
    );
  }, [card, cardExpanded, reportedSurface, density, placement]);

  // Track host framing so keyboard and other host-driven opens count
  useEffect(() => {
    if (framed && !previousFramed.current)
      trackCardOpened(card, reportedSurface, density);
    previousFramed.current = framed;
  }, [framed, density, card, reportedSurface]);

  // A row a host such as a chat draws is not a Home or Feed row, so it is not reported seen
  const list = hostSurface
    ? undefined
    : surface === 'web_home'
      ? 'home'
      : 'web_feeds';
  const impressionRef = useFeedImpression(card, list, placement);

  return (
    <div ref={impressionRef}>
      <FeedCardView
        state={
          title
            ? groupState(cardState(card.row), title, preview, outcomeLabel)
            : cardState(card.row)
        }
        currentStatus={() => cardState(card.row).status}
        press={handlers}
        completeElsewhere={completeElsewhere}
        icon={icon}
        todo={todo}
        appName={appName}
        open={open}
        keepOpen={keepOpen}
        onToggle={toggle}
        onDismiss={onDismiss}
        onChat={() => {
          trackCardChatClicked(
            card,
            reportedSurface,
            'open',
            actionCardAnalyticsContext(reportedSurface, {
              card_expanded: cardExpanded,
            })
          );
          onOpenCardChat();
        }}
        onOpenUrl={handlers.open}
        onOpenSource={onOpenSource}
        meetingSource={meetingSource}
        // A card the user already has a chat about, or one seen in a chat, has no Chat button; ⌘J still opens its chat
        chatless={inChat || hasUserChat(card)}
        customBody={body !== undefined}
        onRetain={onRetain}
        onRunEnd={onRunEnd}
        leavesWhenDone={leavesWhenDone}
        actions={actions}
        snoozedUntil={
          isActionCardSnoozed(card, Date.now())
            ? (card.snoozedUntil ?? undefined)
            : undefined
        }
        onPin={() => void togglePin()}
        onSnooze={() => setSnoozeOpen(true)}
        rowActions={
          <>
            <FeedRowPinSnooze
              card={card}
              surface={reportedSurface}
              snoozeOpen={snoozeOpen}
              onSnoozeOpenChange={setSnoozeOpen}
            />
            <FeedCardMoreMenu
              card={card}
              assign={rowAssign}
              surface={reportedSurface}
              cardExpanded={cardExpanded}
            />
          </>
        }
        more={(source) => (
          <FeedCardMoreMenu
            card={card}
            assign={rowAssign}
            actionable={isActionableStatus(card.status)}
            source={source}
            surface={reportedSurface}
            cardExpanded={cardExpanded}
          />
        )}
        watch={watch}
        keyboardTarget={keyboardTarget}
        subtasks={subtasks}
        lastActiveAt={lastActiveAt}
        frame={(header, run) => (
          <CardV2
            card={card}
            body={body}
            surface={surface}
            deferredDismiss={deferredDismiss}
            reportedSurface={reportedSurface}
            onRun={run.onRun}
            onRunSettled={run.onRunSettled}
            onRunEnd={run.onRunEnd}
            reportsShown={false}
            openCardChat={onOpenInCardChat}
            openPlainCardChat={onOpenCardChat}
            cardExpanded={cardExpanded}
            header={header}
          />
        )}
      />
    </div>
  );
});
